import { useState } from "react";
import { motion } from "framer-motion";
import { Shield, Lock, Send, CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { certificateTypes } from "@/lib/servicesData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    certificate_type: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.certificate_type || !form.message) {
      toast.error("Please fill in all fields");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message sent successfully! We'll get back to you soon.", {
        duration: 5000,
      });
      setForm({ name: "", email: "", certificate_type: "", message: "" });
    } catch (err) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="relative min-h-screen pt-28 pb-24" data-testid="contact-page">
      {/* Background */}
      <div className="absolute inset-0 bg-[#020408]" />
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: "48px 48px",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-blue-950/10 via-transparent to-blue-950/5" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-4 py-1.5 mb-6">
              <Lock className="w-4 h-4 text-blue-400" />
              <span className="text-blue-300 text-xs font-medium tracking-wide uppercase">
                Secure Communication
              </span>
            </div>
            <h1 className="font-outfit font-bold text-4xl sm:text-5xl text-white tracking-tight">
              Get in Touch
            </h1>
            <p className="mt-6 text-slate-400 text-base md:text-lg leading-relaxed">
              Ready to start your certification process? Fill out the form and our team of experts will contact you within 24 hours.
            </p>

            <div className="mt-12 space-y-6">
              {[
                { icon: Shield, title: "Confidential Processing", desc: "All communications are encrypted end-to-end." },
                { icon: CheckCircle, title: "24h Response Time", desc: "Our team reviews every request within one business day." },
                { icon: Lock, title: "Data Protection", desc: "GDPR compliant data handling and storage." },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + i * 0.15 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0">
                    <item.icon className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-outfit font-semibold text-white text-sm">{item.title}</h4>
                    <p className="text-slate-500 text-sm mt-0.5">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-[#0F1116]/60 backdrop-blur-xl border border-white/[0.06] rounded-2xl p-8 md:p-10">
              {sent ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-10"
                  data-testid="contact-success"
                >
                  <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-emerald-400" />
                  </div>
                  <h3 className="font-outfit font-semibold text-2xl text-white">Message Sent</h3>
                  <p className="mt-3 text-slate-400">
                    Thank you for reaching out. Our team will review your request and respond within 24 hours.
                  </p>
                  <button
                    data-testid="send-another-btn"
                    onClick={() => setSent(false)}
                    className="mt-6 text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
                  >
                    Send Another Message
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="flex items-center gap-2 mb-2">
                    <Lock className="w-4 h-4 text-slate-600" />
                    <span className="text-slate-600 text-xs">Encrypted Transmission</span>
                  </div>

                  {/* Name */}
                  <div>
                    <label className="text-slate-300 text-sm font-medium mb-2 block">Full Name</label>
                    <div className="relative">
                      <input
                        name="name"
                        type="text"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        data-testid="contact-name"
                        className="w-full bg-[#020408]/60 border border-slate-800 rounded-lg px-4 py-3 text-slate-200 placeholder:text-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 outline-none transition-all text-sm"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="text-slate-300 text-sm font-medium mb-2 block">Email Address</label>
                    <input
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="john@company.com"
                      data-testid="contact-email"
                      className="w-full bg-[#020408]/60 border border-slate-800 rounded-lg px-4 py-3 text-slate-200 placeholder:text-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 outline-none transition-all text-sm"
                    />
                  </div>

                  {/* Certificate Type */}
                  <div>
                    <label className="text-slate-300 text-sm font-medium mb-2 block">Certificate Type</label>
                    <select
                      name="certificate_type"
                      value={form.certificate_type}
                      onChange={handleChange}
                      data-testid="contact-certificate-type"
                      className="w-full bg-[#020408]/60 border border-slate-800 rounded-lg px-4 py-3 text-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 outline-none transition-all text-sm appearance-none cursor-pointer"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 12px center",
                        backgroundSize: "16px",
                      }}
                    >
                      <option value="" className="bg-[#0F1116] text-slate-500">Select a certificate type</option>
                      {certificateTypes.map((type) => (
                        <option key={type.value} value={type.value} className="bg-[#0F1116] text-slate-200">
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="text-slate-300 text-sm font-medium mb-2 block">Message</label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Tell us about your certification needs..."
                      rows={5}
                      data-testid="contact-message"
                      className="w-full bg-[#020408]/60 border border-slate-800 rounded-lg px-4 py-3 text-slate-200 placeholder:text-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 outline-none transition-all text-sm resize-none"
                    />
                  </div>

                  {/* Submit */}
                  <motion.button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-submit"
                    whileTap={{ scale: 0.98 }}
                    className="w-full relative overflow-hidden bg-blue-600 hover:bg-blue-500 disabled:bg-blue-600/50 text-white font-medium py-3.5 rounded-md transition-all duration-300 hover:shadow-[0_0_30px_rgba(59,130,246,0.4)] flex items-center justify-center gap-2"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Send Secure Message
                      </>
                    )}
                  </motion.button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
